/**
 * CreatorPresenceBadge — discreet "última conexión" + chat-notif hint
 * shown in the public creator profile modal.
 *
 * Visibility rules
 * ────────────────
 * The component renders ONLY when there's an authenticated user (admin /
 * consumer / creator). For anonymous visitors it returns null — the
 * underlying `/api/creators/{id}/presence` endpoint requires auth anyway,
 * so a public visitor would just see a flashing 401.
 *
 * Layout
 * ──────
 * Stays below the model name in TipPage. Pure text, small (text-[11px]),
 * grey-400 — designed to be sutil and not steal visual weight from the
 * primary CTAs (Like / Subscribe / Tip).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, BellOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { API } from "../lib/api";
/**
 * Convierte un ISO-8601 UTC en una etiqueta "12 mayo 2026 a las 14:30"
 * usando Intl con timezone Europa/Madrid. Devuelve "" si la entrada es
 * inválida — evita romper el JSX con "Invalid Date".
 */
function formatMadrid(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const fmt = new Intl.DateTimeFormat("es-ES", {
      timeZone: "Europe/Madrid",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    // "12 de mayo de 2026, 14:30" → "12 mayo 2026 a las 14:30"
    const parts = fmt.formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    return `${get("day")} ${get("month")} ${get("year")} a las ${get("hour")}:${get("minute")}`;
  } catch {
    return "";
  }
}

export default function CreatorPresenceBadge({ creatorIdOrSlug, ownerUserId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  // Bail out for anonymous visitors and when the prop hasn't been resolved
  // yet (TipPage initially renders with the slug from the URL).
  const visible = Boolean(user && creatorIdOrSlug);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const fetchPresence = async () => {
      try {
        const { data } = await axios.get(
          `${API}/creators/${encodeURIComponent(creatorIdOrSlug)}/presence`,
          { withCredentials: true },
        );
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      }
    };
    fetchPresence();
    // Poll every 45 s so the badge transitions to "última conexión" within
    // a reasonable window when the creator logs out / closes the app, and
    // flips back to "● En línea ahora" when they come back. The backend
    // refreshes `last_login_at` on every authenticated request (throttled
    // server-side to once per 30 s), so 45 s gives enough margin.
    const id = setInterval(fetchPresence, 45 * 1000);
    // Also refetch when the tab regains focus — most common UX moment.
    const onFocus = () => fetchPresence();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [visible, creatorIdOrSlug]);

  // Models can only see THEIR OWN presence — backend doesn't enforce that
  // (admin/fan/model can read any), but the spec explicitly says
  // "Modelos ven solo la suya". We gate at the UI level here.
  if (user?.role === "creator" && ownerUserId && user.user_id !== ownerUserId) {
    return null;
  }
  if (!visible || !data) return null;

  const { last_login_at, is_online_now, chat_notifications_enabled } = data;
  const formatted = formatMadrid(last_login_at);

  return (
    <div
      className="mt-1.5 text-[11px] text-zinc-500 font-body leading-snug space-y-0.5"
      data-testid="creator-presence-badge"
    >
      <div data-testid="creator-presence-last-seen">
        {is_online_now ? (
          <span className="text-emerald-400 font-medium">● En línea ahora</span>
        ) : formatted ? (
          <>Última conexión: <span className="text-zinc-400">{formatted}</span></>
        ) : (
          <span className="text-zinc-600">Sin actividad reciente</span>
        )}
      </div>
      <div
        className="flex items-center gap-1"
        data-testid="creator-presence-chat-notifs"
      >
        {chat_notifications_enabled ? (
          <>
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            <span className="text-emerald-500/80">Notificaciones activadas</span>
          </>
        ) : (
          <>
            <BellOff className="w-3 h-3 text-zinc-600" />
            <span className="text-zinc-600">No tiene notificaciones activas</span>
          </>
        )}
      </div>
    </div>
  );
}
